// Calendar-date helpers. Dates are plain 'YYYY-MM-DD' strings (no time zone) so
// that a lease date never shifts by a day because of the clock or the server's zone.

export type ISODate = string;

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isISODate(value: unknown): value is ISODate {
  if (typeof value !== 'string' || !ISO_RE.test(value)) return false;
  return format(parse(value)) === value;
}

function parse(d: ISODate): Date {
  if (!ISO_RE.test(d)) throw new Error(`Not a YYYY-MM-DD date: ${d}`);
  const [y, m, day] = d.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, day));
}

function format(d: Date): ISODate {
  return d.toISOString().slice(0, 10);
}

export function addDays(d: ISODate, days: number): ISODate {
  const x = parse(d);
  x.setUTCDate(x.getUTCDate() + days);
  return format(x);
}

/** Adds calendar months; the day is clamped to the end of the month (Jan 31 + 1 month = Feb 28/29). */
export function addMonths(d: ISODate, months: number): ISODate {
  const [y, m, day] = d.split('-').map(Number) as [number, number, number];
  const firstOfTarget = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth() + 1, 0),
  ).getUTCDate();
  firstOfTarget.setUTCDate(Math.min(day, lastDay));
  return format(firstOfTarget);
}

export function addYears(d: ISODate, years: number): ISODate {
  return addMonths(d, years * 12);
}

/** 'YYYY-MM' bucket for monthly reporting. */
export function monthOf(d: ISODate): string {
  return d.slice(0, 7);
}

export function maxDate(dates: ISODate[]): ISODate | null {
  return dates.length === 0 ? null : dates.reduce((a, b) => (a > b ? a : b));
}
